import type { TaskDto } from "@albertine/types";
import { Lib } from "albertine-shared-web";
import { addDays } from "date-fns";
import type { FirebaseTimestamp } from "../common/types/FirebaseTimestamp";
import type { InternalNoteWithMention } from "../common/types/Message";
import type { Request } from "../common/types/Request";
import type { SortOrder, SortSettings } from "../common/types/Sort";
import { sortByNumberComparator } from "../common/utils/sort.util";
import { combineDateAndTimeString } from "./time.util";
import { compareTo, fromTimestampToDate, timestampFromDate } from "./timestamp.util";

export const sortRequestsByEventDate = (requests: Request[], order: SortOrder) => {
    const comparator = (a: Request, b: Request) =>
        Lib.Utils.Sorting.sortByDateComparator<Request>((r) => {
            const combined = combineDateAndTimeString(
                r?.startDate ?? null,
                r?.startTime ?? null,
            );
            const date = combined ?? r?.startDate ?? undefined;

            return date ? new Date(date) : undefined;
        })(a, b);

    return order === "asc"
        ? requests.sort(comparator)
        : requests.sort(comparator).reverse();
};

export function sortRequests(requests: Request[], settings: SortSettings): Request[] {
    const unassigned = requests.filter((r) => !r.assignedTo);
    const assigned = requests.filter((r) => r.assignedTo);

    const defaultReturn = (() => {
        const comparator = (a: Request, b: Request) =>
            Lib.Utils.Sorting.sortByDateComparator<Request>((r) => {
                const combined = combineDateAndTimeString(
                    r?.startDate ?? null,
                    r?.startTime ?? null,
                );
                const date = combined ?? r?.startDate ?? undefined;

                return date ? new Date(date) : undefined;
            }, true)(a, b);
        const unassignedSorted = unassigned.sort(comparator);
        const assignedSorted = assigned.sort(comparator);

        return settings.order === "asc"
            ? [...unassignedSorted, ...assignedSorted]
            : [...unassignedSorted.reverse(), ...assignedSorted.reverse()];
    })();

    switch (settings.by) {
        case "ticketNumber": {
            const comparator = (a: Request, b: Request) =>
                sortByNumberComparator<Request>((r) => r?.ticketNumber ?? 0)(a, b);
            const unassignedSorted = unassigned.sort(comparator);
            const assignedSorted = assigned.sort(comparator);

            return settings.order === "asc"
                ? [...unassignedSorted, ...assignedSorted]
                : [...unassignedSorted.reverse(), ...assignedSorted.reverse()];
        }
        case "deadline": {
            const comparator = (a: Request, b: Request) =>
                Lib.Utils.Sorting.sortByDateComparator<Request>((r) =>
                    r?.nextDeadlineAt
                        ? fromTimestampToDate(r.nextDeadlineAt)
                        : undefined,
                )(a, b);
            const unassignedSorted = unassigned.sort(comparator);
            const assignedSorted = assigned.sort(comparator);

            return settings.order === "asc"
                ? [...unassignedSorted, ...assignedSorted]
                : [...unassignedSorted.reverse(), ...assignedSorted.reverse()];
        }
        case "eventDate":
        default:
            return defaultReturn;
    }
}

function taskDueDateComparator(a: TaskDto, b: TaskDto): number {
    return (
        new Date(a.mentionDueDate!).getTime() - new Date(b.mentionDueDate!).getTime()
    );
}

function taskCreatedAtComparator(a: TaskDto, b: TaskDto): number {
    return (
        new Date(b.mentionCreatedAt).getTime() - new Date(a.mentionCreatedAt).getTime()
    );
}

export function sortTasksInProfile(
    tasks: TaskDto[],
    now: Date = new Date(),
): TaskDto[] {
    const nowPlus24hDate = addDays(now, 1);
    // split tasks to those with due date and those without
    const tasksWithDueDate: TaskDto[] = [];
    const tasksWithoutDueDate: TaskDto[] = [];
    tasks.forEach((task) => {
        if (task.mentionDueDate) {
            tasksWithDueDate.push(task);
        } else {
            tasksWithoutDueDate.push(task);
        }
    });
    // split tasks with due date to those where the due date is in the future by 24 hours or more and the rest
    const tasksWithDueDateInFuture: TaskDto[] = [];
    const tasksWithDueDateInPast: TaskDto[] = [];
    tasksWithDueDate.forEach((task) => {
        if (new Date(task.mentionDueDate!) > nowPlus24hDate) {
            tasksWithDueDateInFuture.push(task);
        } else {
            tasksWithDueDateInPast.push(task);
        }
    });
    return [
        ...tasksWithDueDateInPast.sort(taskDueDateComparator),
        ...tasksWithoutDueDate.sort(taskCreatedAtComparator),
        ...tasksWithDueDateInFuture.sort(taskDueDateComparator),
    ];
}

type SortableKeys<T> = {
    [K in keyof T]: T[K] extends string | number ? K : never;
}[keyof T];

export function sortAbc<T extends object>(sortProperty: SortableKeys<T>) {
    return (a: T, b: T): number => {
        const valueA = String(a[sortProperty]).toLowerCase();
        const valueB = String(b[sortProperty]).toLowerCase();

        if (valueA < valueB) {
            return -1;
        }
        if (valueA > valueB) {
            return 1;
        }
        return 0;
    };
}

// Generic function to extract sentAt date from objects with direct sentAt property
const getSentAtFromItem = (
    item: { sentAt?: FirebaseTimestamp } | undefined,
): Date | undefined => {
    if (item && "sentAt" in item && item.sentAt) {
        return fromTimestampToDate(item.sentAt);
    }
    return undefined;
};

// Export comparator for direct sentAt items (most recent first) - following threads.util.ts pattern
export const compareBySentAt = Lib.Utils.Sorting.sortByDateComparator(
    getSentAtFromItem,
    true,
);
